// src/services/shop/shopCostsService.ts
import { supabase } from '@/lib/supabase';

export type CostRecurrence = 'one_off' | 'monthly' | 'quarterly' | 'yearly' | 'custom_days';

export interface ShopCost {
    id: string;
    shop_id: number;
    name: string;
    category: string | null;
    amount: number;
    recurrence: CostRecurrence;
    interval_days: number | null;
    start_date: string | null;
    active: boolean;
    note: string | null;
    created_at: string;
}

export const fetchShopCostsService = async (shopId: number) => {
    const { data, error } = await supabase
        .from('ShopCosts')
        .select('*')
        .eq('shop_id', shopId)
        .order('created_at', { ascending: false });
    if (error) return { success: false, error: error.message, data: [] as ShopCost[] };
    return { success: true, data: (data || []) as ShopCost[] };
};

export const createShopCostService = async (payload: {
    shop_id: number;
    name: string;
    category?: string | null;
    amount: number;
    recurrence: CostRecurrence;
    interval_days?: number | null;
    start_date?: string | null;
    note?: string | null;
}) => {
    const { data, error } = await supabase.from('ShopCosts').insert(payload).select().single();
    if (error) return { success: false, error: error.message };
    return { success: true, data: data as ShopCost };
};

export const updateShopCostService = async (
    id: string,
    patch: {
        name?: string;
        category?: string | null;
        amount?: number;
        recurrence?: CostRecurrence;
        interval_days?: number | null;
        start_date?: string | null;
        note?: string | null;
    },
) => {
    const { data, error } = await supabase.from('ShopCosts').update(patch).eq('id', id).select().single();
    if (error) return { success: false, error: error.message };
    return { success: true, data: data as ShopCost };
};

export const deleteShopCostService = async (id: string) => {
    const { error } = await supabase.from('ShopCosts').delete().eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
};

/**
 * Equivalente mensile di un costo, in base alla ricorrenza.
 * one_off non ha un equivalente mensile ricorrente → 0.
 */
export const monthlyizeCost = (c: Pick<ShopCost, 'amount' | 'recurrence' | 'interval_days' | 'active'>): number => {
    if (c.active === false) return 0;
    const amount = Number(c.amount) || 0;
    switch (c.recurrence) {
        case 'monthly':
            return amount;
        case 'quarterly':
            return amount / 3;
        case 'yearly':
            return amount / 12;
        case 'custom_days':
            return c.interval_days && c.interval_days > 0 ? amount * (30.42 / c.interval_days) : 0;
        case 'one_off':
        default:
            return 0;
    }
};
